/*
 * The tutorial's script.
 *
 * Nothing here is staged. Each lesson names a place, an instant and a camera,
 * and animates one of them; the sky is then computed through exactly the same
 * transforms the game uses. So when latitude sweeps north and Polaris climbs,
 * it climbs because the maths says so, and the angle the text claims is the
 * angle on screen. A faked animation would teach the wrong thing, and this is
 * the one place where being wrong would be worse than being dull.
 *
 * Each lesson supplies:
 *   state(t)  the observer at animation progress t in [0, 1]
 *   camera(t) where to look
 *   guides    which overlay graphics to draw
 */

import { localSolarTime } from '../format.mjs';
import { ASTRONOMICAL_TWILIGHT_DEG, sunAltitude } from '../astronomy.mjs';
import { starClockLabel } from './guides.mjs';

const DAY = 86400000;

// A fixed instant keeps the lessons reproducible. Late evening in October puts
// the Plough, Cassiopeia, Orion and the Southern Cross all within reach.
export const BASE_DATE = new Date('2026-10-15T21:00:00Z');

const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function dayLabel(date) {
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

/*
 * Lesson six holds one instant and pans a full turn of longitude, and it is the
 * only lesson that deliberately walks the observer into daylight. That is the
 * point being made — a sky with stars in it is a sky on the night side of the
 * Earth — and it cannot be made from the dark alone. Twenty degrees north keeps
 * twilight short, so the change reads as day giving way to night rather than as
 * a long grey fade, and midnight lands on the Greenwich meridian, which puts the
 * dark half in the middle of the pan.
 *
 * The pan wraps, so the loop is seamless: a hundred and eighty east is a hundred
 * and eighty west.
 */
const DAYNIGHT_INSTANT = new Date('2026-10-16T00:00:00Z');
const DAYNIGHT_LAT = 20;

/*
 * The star clock's own night: 60 N on the winter solstice, from the end of
 * evening twilight to the start of morning twilight. That window is twelve hours
 * — exactly half a turn of the dial — and it is about as much of the dial as any
 * one night anywhere will give you, which is a thing the lesson says out loud.
 * The Sun never comes within two degrees of the astronomical twilight line
 * inside it.
 */
const CLOCK_NIGHT = Date.UTC(2026, 11, 21, 18, 0, 0);
const CLOCK_HOURS = 12;

/*
 * The drift lesson stands at 40 N on the Greenwich meridian and looks at nine in
 * the evening, night after night, from the first of September to the first of
 * April. Seven months, and the hand creeps almost fourteen hours round the dial
 * over them — the four-minutes-a-day gap between the solar day and the sidereal
 * one, made visible.
 *
 * The date is stepped in whole days rather than swept continuously, because a
 * continuous sweep would run the clock through every hour of the day as well as
 * through the calendar, and half of those hours are daylight. Whole days keep it
 * at nine in the evening throughout, which is the only way the lesson's claim —
 * same hour, different sky — is the thing being shown.
 */
const DRIFT_START = Date.UTC(2026, 8, 1, 21, 0, 0);
const DRIFT_DAYS = 212;

/*
 * Where and when the explorer opens: Denver, tonight.
 *
 * It used to open on the October night the longitude lesson leaves you standing
 * on, which continued that lesson's sky rather than starting cold. Tonight is
 * worth more than the continuity: this is the page that invites you to go and
 * look, and the sky it opens on is now the one actually outside.
 *
 * The hour is local midnight rather than a fixed UTC hour, and that is what
 * makes the date safe to float. A fixed hour is dark only for part of the year —
 * the 3 UTC this used to use is 8pm in Denver, which is astronomical night in
 * October and broad twilight in August, so simply swapping the date would have
 * opened the last lesson on a washed-out sky roughly half the time. Local
 * midnight is the darkest instant of any date, and at this latitude it clears
 * astronomical twilight on every day of the year, with 27 degrees to spare at
 * the summer solstice. A test walks the lesson and insists on the dark, so a
 * fixed hour would have made that test fail depending on the day it ran.
 */
const EXPLORER_PLACE = { lat: 39.7, lon: -105 };

const YEAR = new Date().getUTCFullYear();

function todayOfYear() {
  const now = new Date();
  return Math.floor(
    (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) -
      Date.UTC(now.getUTCFullYear(), 0, 1)) / DAY
  );
}

export const EXPLORER_START = {
  ...EXPLORER_PLACE,
  dayOfYear: todayOfYear(),
  // Derived from the longitude, so moving the opening place cannot silently
  // leave the opening hour pointing at somebody else's midnight.
  hour: (((-EXPLORER_PLACE.lon / 15) % 24) + 24) % 24
};

export function explorerDate({ dayOfYear, hour }) {
  return new Date(Date.UTC(YEAR, 0, 1 + dayOfYear, 0, 0, 0) + hour * 3600000);
}

/*
 * Three places within a degree of the same latitude, at one instant when all
 * three are genuinely in astronomical night. Holding latitude still is what
 * makes the lesson legible: everything that changes between them is longitude.
 *
 * The instant matters. At most times one of the three is in daylight, and
 * drawing its stars anyway would be teaching from a sky that could not be seen.
 */
const LONGITUDE_INSTANT = new Date('2026-10-16T03:00:00Z');

const LONGITUDE_STOPS = [
  { name: 'Madrid', lat: 40.4, lon: -3.7 },
  { name: 'New York', lat: 40.7, lon: -74.0 },
  { name: 'Denver', lat: 39.7, lon: -105.0 }
];

/*
 * Walks the stops there and back rather than looping around, so the longest move
 * is one leg. Cycling would have to swing a hundred degrees back to the start in
 * one go, which is the spin that made the earlier version of this lesson
 * unreadable: a continuous sweep shows a blur, not two observers.
 */
const STOP_ORDER = [0, 1, 2, 1];

/*
 * Most of each leg is spent sitting still. The point is made by reading two
 * skies, not by watching the move between them, and a fast slide is what made
 * the first version of this lesson unwatchable.
 */
const HOLD = 0.6;

function atStop(t) {
  const legs = STOP_ORDER.length;
  const position = t * legs;
  const leg = Math.floor(position) % legs;
  const within = position - Math.floor(position);

  const from = LONGITUDE_STOPS[STOP_ORDER[leg]];
  const to = LONGITUDE_STOPS[STOP_ORDER[(leg + 1) % legs]];

  if (within < HOLD) return describeStop(from.name, from.lat, from.lon);

  const k = ease((within - HOLD) / (1 - HOLD));
  return describeStop(
    k < 0.5 ? from.name : to.name,
    from.lat + (to.lat - from.lat) * k,
    from.lon + (to.lon - from.lon) * k
  );
}

function describeStop(name, lat, lon) {
  return { name, lat, lon, date: LONGITUDE_INSTANT, clock: localSolarTime(LONGITUDE_INSTANT, lon) };
}
const mix = (a, b, t) => a + (b - a) * ease(t);

export const LESSONS = [
  {
    id: 'turning',
    title: 'Finding North and South',
    blurb: [
      'Because the earth rotates around its north-south axis, the northernmost point and ' +
        'southernmost points in the sky stay fixed throughout the night.',
      "In the Northern hemisphere, we use Polaris, also known as the North Star. It's the " +
        "star at the end of the Little Dipper's handle, a constellation also known as Ursa Minor.",
      "In the Southern hemisphere, there is the Southern Cross, also known as the " +
        "constellation Crux. It's four unusually bright stars, and it wheels around the " +
        'southern point through the night rather than sitting on it, but its long axis ' +
        'always points towards the celestial south pole.'
    ],
    caption: (t) => `${(t * 6).toFixed(1)} hours later`,
    duration: 14000,
    loop: true,
    state: (t) => ({ lat: 45, lon: 0, date: new Date(BASE_DATE.getTime() + t * 6 * 3600000) }),
    // Aimed low enough to keep the ground line in frame. The pole means nothing
    // without the horizon to measure it against, and the latitude lesson does
    // exactly that measurement, so the horizon is worth establishing early.
    camera: () => ({ az: 0, alt: 30, fov: 95 }),
    guides: ['pole']
  },
  {
    id: 'polaris',
    title: 'Finding Polaris',
    blurb:
      "You can find Polaris by finding the Big Dipper (aka Ursa Major), and taking the two " +
      "stars at the end of the bowl and following them across the sky until you've hit Polaris.",
    caption: () => 'The pointers, and where they lead',
    duration: 9000,
    // Three in the morning, when the Plough has swung up into the north-east and
    // sits clear of the horizon alongside Polaris. At the base hour it is down in
    // the murk and the pointers run off the bottom of the frame.
    state: () => ({ lat: 45, lon: 0, date: new Date('2026-10-16T03:00:00Z') }),
    camera: () => ({ az: 18, alt: 33, fov: 88 }),
    guides: ['pointers', 'polaris']
  },
  {
    id: 'southern',
    title: 'Finding the Southern Cross',
    blurb:
      'There is no bright star over the south pole of the sky. Instead, find the Southern ' +
      'Cross - 4 bright stars in a cross shape - and follow its long axis downwards about ' +
      'four and a half times its own length.',
    caption: () => 'The long axis, and where it points',
    duration: 9000,
    /*
     * Held still, like the Polaris lesson it mirrors. This is a lesson about
     * finding a shape and following a line, and both are easier to read on a sky
     * that is not also moving; the latitude that shape measures is the next
     * lesson's job, and that one sweeps.
     *
     * Early April rather than October, and the small hours rather than the
     * evening. In October the Cross crosses the meridian in broad daylight from
     * this longitude, so the earlier instant here was drawing a sky full of
     * stars over a coastline that the inset map showed sitting in late
     * afternoon sunshine. At 30 S the Sun is 38 degrees down and the Cross
     * stands at 42, high enough to read and well short of the zenith.
     */
    state: () => ({ lat: -30, lon: -70, date: new Date('2026-04-05T08:00:00Z') }),
    // Swung west of the Cross so it and the pole marker sit right of centre
    // rather than crowding the left edge.
    camera: () => ({ az: 194, alt: 32, fov: 96 }),
    guides: ['cross', 'southpole']
  },
  {
    id: 'latitude',
    title: 'Identifying Latitude',
    blurb: [
      "Once you've found Polaris (north) or the Southern Cross (south), you know your " +
        'hemisphere. But you can also use them to find your latitude. The higher they are in ' +
        'the sky, the farther you are from the equator.',
      'On the equator Polaris sits on the horizon; at the pole it is straight overhead. Watch ' +
        'it climb as we sail north.'
    ],
    caption: (t, state) => `${Math.abs(state.lat).toFixed(0)}° N`,
    duration: 16000,
    loop: true,
    state: (t) => ({ lat: mix(2, 62, bounce(t)), lon: 0, date: BASE_DATE }),
    /*
     * Aimed lower than the other lessons, which lifts the horizon well clear of
     * the lesson dots along the bottom: this is the one camera where they fell
     * below the ground line and read as being buried in it. The field is widened
     * to match, because the sweep has to keep Polaris in frame all the way up to
     * 62 N while the horizon it is measured from stays on screen.
     */
    camera: () => ({ az: 0, alt: 27, fov: 100 }),
    guides: ['polaris', 'altitude']
  },
  {
    id: 'cruxlatitude',
    title: 'Identifying Latitude in the South',
    blurb: [
      'The same measurement works south of the equator, with one extra step: there is no ' +
        'star on the pole, so follow the long axis of the Cross first and measure the empty ' +
        'point it leads you to.',
      'On the equator the celestial south pole sits on the horizon; at the pole it is ' +
        'straight overhead. Watch it climb as we sail south.'
    ],
    caption: (t, state) => `${Math.abs(state.lat).toFixed(0)}° S`,
    duration: 16000,
    loop: true,
    /*
     * The same April night the Cross was found on, so the sky the last lesson
     * held still is the one that now starts moving. It is astronomical night at
     * every latitude the sweep visits — the Sun is 41 degrees down at the
     * shallow end and still 23 down at 62 S, its highest anywhere on the run.
     */
    state: (t) => ({ lat: mix(-2, -62, bounce(t)), lon: -70, date: new Date('2026-04-05T08:00:00Z') }),
    /*
     * The mirror of the lesson above, down to the numbers: aimed low, with a
     * field wide enough to carry the marker to 62 degrees while the horizon it
     * is measured from stays clear of the lesson dots.
     *
     * The Cross is not ringed here, which is deliberate and is also what makes
     * this camera possible. The finding aids belong to the finding lessons —
     * the northern twin does not redraw the Plough pointers either — and the
     * Cross both climbs and drifts west as the sweep runs south, so keeping a
     * label on it meant either stopping at 48 S or letting the label slide in
     * behind the inset map in the top right corner. The measurement is due
     * south and stays on the camera axis, where a rectilinear projection
     * reports height honestly.
     */
    camera: () => ({ az: 180, alt: 27, fov: 100 }),
    guides: ['southpole', 'altitude']
  },
  {
    id: 'daynight',
    title: 'Finding Longitude',
    blurb: [
      'Now that we know our latitude, we need our longitude.',
      'The first thing to notice is that if we see stars, it must be night! So we have to be ' +
        "in a dark area of the globe. The game gives the local time of each area you click on, " +
        "so if it's daytime, it can't be the right answer."
    ],
    /*
     * Reports what the sky is doing rather than only the hour, because the whole
     * lesson is the pairing of the two: the stars go out at a particular clock
     * reading, and come back at another.
     */
    caption: (t, state) => {
      const sun = sunAltitude(state.lat, state.lon, state.date);
      const sky = sun > -0.5
        ? 'the Sun is up'
        : sun > ASTRONOMICAL_TWILIGHT_DEG
          ? 'twilight'
          : 'stars out';
      return `${localSolarTime(state.date, state.lon)} local · ${sky}`;
    },
    duration: 23000,
    loop: true,
    // One instant, one latitude, all the way round. Everything that changes is
    // longitude, which is the only way to show that longitude is what sets the hour.
    state: (t) => ({ lat: DAYNIGHT_LAT, lon: -180 + t * 360, date: DAYNIGHT_INSTANT }),
    // West, and low. The Sun is not drawn as a disc, so what sells the crossing
    // is the whole sky lifting off the ground line and going back down again.
    camera: () => ({ az: 250, alt: 24, fov: 105 }),
    guides: []
  },
  {
    id: 'starclock',
    title: 'The stars near the pole are a clock',
    blurb: [
      'The stars around the celestial pole rotate around it once a day.' +
        ' You can use this to treat it like a 24-hour clock - you just have to know its orientation.' +
        ' The orientation is based on both your location and the time of year.',
      'In the north the hand is Ursa Minor, the Little Dipper.' +
        ' In the south, the Southern Cross does the same job around the empty pole.',
    ],
    /*
     * Prints the clock beside the hand rather than the elapsed time, because the
     * lesson's new claim is that those two numbers are the same number. Saying
     * "3.0 hours later" put the evidence for that just out of reach.
     */
    caption: (t, state) =>
      `${localSolarTime(state.date, state.lon)} local · the hand at ${starClockLabel(state)}h`,
    duration: 20000,
    loop: true,
    state: (t) => ({ lat: 60, lon: 0, date: new Date(CLOCK_NIGHT + t * CLOCK_HOURS * 3600000) }),
    /*
     * Aimed square at the pole, and no wider than the dial needs. The horizon is
     * out of frame and that is deliberate: this is the one pair of lessons whose
     * reference direction is not the ground but the line straight up from the
     * pole, and a camera with no roll puts that line vertically on screen.
     */
    camera: () => ({ az: 0, alt: 56, fov: 80 }),
    guides: ['starclock']
  },
  {
    id: 'drift',
    title: 'Reading the star clock',
    blurb: [
      "The “clock's” orientation changes throughout the year.",
      "The game's date is always today. You'll need to " +
        'remember how the “clock” is oriented for this time of year. The date says where ' +
        'the dial starts; the hand says how far past that you are. Together they are your ' +
        'local time.'
    ],
    caption: (t, state) =>
      `${dayLabel(state.date)} · ${localSolarTime(state.date, state.lon)} local · ` +
      `the hand at ${starClockLabel(state)}h`,
    duration: 20000,
    loop: true,
    // Whole days only, so the hour of the evening never moves. Out and back, so
    // the loop does not snap seven months in one frame.
    state: (t) => ({
      lat: 40,
      lon: 0,
      date: new Date(DRIFT_START + Math.round(bounce(t) * DRIFT_DAYS) * DAY)
    }),
    camera: () => ({ az: 0, alt: 40, fov: 78 }),
    guides: ['starclock']
  },
  {
    id: 'longitude',
    title: 'Identifying Longitude',
    blurb: [
      'Here is the payoff. These three places sit at the same latitude, and this is one single ' +
        'instant — the very same moment for all three. The hand reads differently at each.',
      'Madrid and Denver are 101 degrees of longitude apart, and their hands sit ~seven ' +
        'hours apart on the dial.',
      'So: read the hand, and with the date it gives you your local time. Then hunt along the ' +
        'map for a longitude whose local time matches. The game prints the local time of ' +
        'everywhere you click, so you can check before you commit.'
    ],
    caption: (t, state) =>
      `${state.name} · ${state.clock} local · the hand at ${starClockLabel(state)}h`,
    duration: 28000,
    loop: true,
    state: (t) => atStop(t),
    camera: () => ({ az: 0, alt: 40, fov: 78 }),
    guides: ['starclock']
  },
  {
    id: 'explorer',
    title: 'Now try it yourself',
    interactive: true,
    blurb: [
      'Everything from here on is yours. Drag the pin anywhere on Earth and the sky will travel ' +
        'with it; move the clock and the whole thing turns, fifteen degrees an hour.',
      'Some things to go looking for. Slide north and watch the pole marker climb by exactly the ' +
        'degrees of latitude you gain. Cross the equator and watch it sink and come back up on the ' +
        'other side of the sky. Hold the clock still and slide east instead — the sky changes just ' +
        'as much, which is the whole reason longitude needs one.',
      'The shadow on the map is real night. Drag into the lit half and the stars go out, because ' +
        'they would.'
    ],
    caption: () => '',
    duration: 1000,
    loop: false,
    state: () => ({ ...EXPLORER_START, date: explorerDate(EXPLORER_START) }),
    /*
     * Opens facing the pole, because the pole marker is the thing the copy above
     * tells you to watch. Aimed south it was drawn behind the camera and never
     * appeared at all, so the first instruction on the page pointed at nothing.
     * The view can be dragged from here.
     */
    camera: () => ({ az: 0, alt: 30, fov: 100 }),
    guides: ['pole']
  }
];

// Runs the parameter out and back, so a looping lesson does not snap.
function bounce(t) {
  return t < 0.5 ? t * 2 : 2 - t * 2;
}

export { DAY };
